import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useSendProgress, useSetProductChangeCall } from "./service";

import Header from "./Header";

import Grid2 from "@mui/material/Grid2";
import {
  Box,
  Dialog,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import LoadingWave from "./LoadingWave";
import Loading from "./Loading";
import MeetingScheduler from "./MeetingScheduler";

function deepMergeWithPriority(target, source) {
  if (!target || typeof target !== "object") return source;
  if (!source || typeof source !== "object") return target;

  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      if (!target[key]) target[key] = {};
      target[key] = deepMergeWithPriority(target[key], source[key]);
    } else if (target[key] === undefined) {
      target[key] = source[key];
    }
  }
  return target;
}

function mergeSectionWithTemplates(sectionData, templates) {
  if (!sectionData || !templates) return sectionData;
  const clonedSectionData = structuredClone(sectionData);

  clonedSectionData.sections?.forEach((section) => {
    section.components?.forEach((component) => {
      if (component.is_template) {
        const template = templates.find(
          (t) => t.template_id === component.template_id
        );
        if (template) {
          Object.assign(
            component,
            deepMergeWithPriority(component, structuredClone(template))
          );
        }
      }

      component.elements =
        component.elements?.map((element) => {
          const elementTemplate = templates.find(
            (t) => t.template_id === element.template_id
          );
          if (elementTemplate) {
            return {
              ...deepMergeWithPriority(
                element,
                structuredClone(elementTemplate)
              ),
            };
          }
          return element;
        }) || [];
    });
  });

  return clonedSectionData;
}

const getScreenTypeValue = (screenWidth, screenHeight, screenTypeDetails) => {
  if (!Array.isArray(screenTypeDetails)) return null;

  const aspectRatio = screenWidth / screenHeight;

  const matchingScreenTypes = screenTypeDetails.filter((type) => {
    const arLimit = parseFloat(type.aspect_ratio_lower_limit) || 0;
    const minWidth = parseFloat(type.minimum_width) || 0;
    return aspectRatio >= arLimit && screenWidth >= minWidth;
  });

  if (matchingScreenTypes.length > 0) {
    const sorted = matchingScreenTypes.sort((a, b) => {
      const minWidthDiff =
        parseFloat(b.minimum_width) - parseFloat(a.minimum_width);
      if (minWidthDiff !== 0) return minWidthDiff;
      return (
        parseFloat(b.aspect_ratio_lower_limit) -
        parseFloat(a.aspect_ratio_lower_limit)
      );
    });
    return sorted[0].screen_type_id;
  }

  const defaultType = screenTypeDetails.find((type) => type.is_default);
  return defaultType?.screen_type_id || null;
};

const useResponsiveScreenType = (screenTypeDetails) => {
  const [screenType, setScreenType] = useState(() =>
    getScreenTypeValue(window.innerWidth, window.innerHeight, screenTypeDetails)
  );

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const type = getScreenTypeValue(width, height, screenTypeDetails);
      setScreenType(type);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [screenTypeDetails]);

  return screenType;
};

const getValueByScreenType = (dataArray, screenTypeId, defaultScreenTypeId) => {
  if (!Array.isArray(dataArray)) return null;

  const match = dataArray.find((item) => item.screen_type_id === screenTypeId);
  if (match) return match;

  return (
    dataArray.find((item) => item.screen_type_id === defaultScreenTypeId) ||
    null
  );
};

const getPropValue = (
  dataArray,
  screenTypeId,
  defaultScreenTypeId,
  fallback = null
) => {
  const match = getValueByScreenType(
    dataArray,
    screenTypeId,
    defaultScreenTypeId
  );
  return match?.value ?? match?.path ?? fallback;
};

const extractElementProps = (element, screenTypeId, defaultScreenTypeId) => {
  const props = {};

  for (const key in element) {
    const val = element[key];

    if (Array.isArray(val) && val.length > 0 && val[0]?.screen_type_id) {
      const resolved = getPropValue(
        val,
        screenTypeId,
        defaultScreenTypeId,
        null
      );
      if (resolved !== null) props[key] = resolved;
    } else if (
      typeof val === "object" &&
      val !== null &&
      !Array.isArray(val) &&
      Object.values(val).some((v) => v?.screen_type_id)
    ) {
      const resolved = getPropValue(
        Object.values(val),
        screenTypeId,
        defaultScreenTypeId,
        null
      );
      if (resolved !== null) props[key] = resolved;
    } else if (
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "boolean"
    ) {
      props[key] = val;
    }
  }

  return props;
};

const computeElementLayout = (
  element,
  screenTypeId,
  defaultScreenTypeId,
  parentWidth,
  parentHeight,
  elementIndex = 0,
  isChild = false,
  margin = 0,
  screenWidth = 0
) => {
  const sizeObj = getValueByScreenType(
    element.size,
    screenTypeId,
    defaultScreenTypeId
  );

  let width = parseFloat(sizeObj?.x || 0) * parentWidth;
  let height = parseFloat(sizeObj?.y || 0) * parentHeight;

  const applyMargin = isChild ? false : element?.is_margin !== false;
  const contentWidth = screenWidth - margin * 2 * screenWidth;
  const leftOffset = margin * screenWidth;

  const layout = {
    element_id: element.element_id,
    type: element.type,
    size: {
      width: applyMargin ? (width * contentWidth) / parentWidth : width,
      height,
    },
    element_index: elementIndex,
    is_margin: element?.is_margin,
    props: extractElementProps(element, screenTypeId, defaultScreenTypeId),
  };

  if (!isChild) {
    const positionObj = getValueByScreenType(
      element.position,
      screenTypeId,
      defaultScreenTypeId
    );

    const top = parseFloat(positionObj?.top || 0) * parentHeight;
    const leftRaw = parseFloat(positionObj?.left || 0) * parentWidth;
    const left = applyMargin
      ? (leftRaw * contentWidth) / parentWidth + leftOffset
      : leftRaw;

    layout.position = { top, left };
  }

  if (Array.isArray(element.children) && element.children.length > 0) {
    layout.children = element.children.map((child, index) =>
      computeElementLayout(
        child,
        screenTypeId,
        defaultScreenTypeId,
        layout.size.width,
        layout.size.height,
        index,
        true,
        margin,
        screenWidth
      )
    );
  }

  return layout;
};

const getDimensionValue = (
  dimensionsArray,
  screenTypeId,
  defaultScreenTypeId
) => {
  if (!Array.isArray(dimensionsArray)) return 1;

  const screenValue =
    dimensionsArray.find((d) => d.screen_type_id === screenTypeId)?.value ||
    dimensionsArray.find((d) => d.screen_type_id === defaultScreenTypeId)
      ?.value;

  return parseFloat(screenValue || 1);
};

const generatePageLayout = (
  storefrontData,
  screenTypeId,
  defaultScreenTypeId,
  screenWidth,
  screenHeight
) => {
  const margin = parseFloat(storefrontData?.frames?.margin || 0);
  const layers = storefrontData?.layers || [];
  const pageLayout = [];

  layers.forEach((layer) => {
    const layerZIndex = (1 + Number(layer.layer_index || 0)) * 1000000;

    const sections = layer.sections || [];
    sections.forEach((section, sectionIndex) => {
      const sectionZIndex = layerZIndex + (sectionIndex + 1) * 10000;

      const sectionWidthRatio = getDimensionValue(
        section?.width,
        screenTypeId,
        defaultScreenTypeId
      );
      const sectionHeightRatio = getDimensionValue(
        section?.height,
        screenTypeId,
        defaultScreenTypeId
      );

      const sectionWidth = sectionWidthRatio * screenWidth;
      const sectionHeight = sectionHeightRatio * screenHeight;

      const computedElements = (section.elements || []).map((element, index) =>
        computeElementLayout(
          element,
          screenTypeId,
          defaultScreenTypeId,
          sectionWidth,
          sectionHeight,
          index,
          false,
          margin,
          screenWidth
        )
      );

      pageLayout.push({
        section_id: section.section_id,
        section_index: section.section_index,
        section_type: section.section_type,
        background_color: section.background_color || "#ffffff",
        zIndex: sectionZIndex,
        elements: computedElements,
        sectionWidth,
        sectionHeight,
        section_text: section?.text,
      });
    });
  });

  return pageLayout;
};

const getElementStyle = (element) => {
  const { size, position, props } = element;

  const {
    justify_content,
    align_items,
    direction,
    background_color,
    is_border,
    border,
    border_color,
    border_radius,
    is_transparent,
    margin_top,
    margin_bottom,
    margin_left,
    margin_right,
    padding_top,
    padding_bottom,
    padding_left,
    padding_right,
    text_align,
    is_visible,
  } = props;

  return {
    position: position ? "absolute" : "relative",
    ...(position?.top && { top: position?.top }),
    ...(position?.left && { left: position?.left }),
    ...(size?.width && { width: size.width }),
    ...(size?.height && { height: size.height }),
    backgroundColor: is_transparent ? "transparent" : background_color,
    border: props.is_border ? `${border}px solid ${border_color}` : "none",
    borderRadius: border_radius || 0,
    marginTop: `${margin_top}px` || 0,
    marginBottom: `${margin_bottom}px` || 0,
    marginLeft: `${margin_left}px` || 0,
    marginRight: `${margin_right}px` || 0,
    paddingTop: `${padding_top}px` || 0,
    paddingBottom: `${padding_bottom}px` || 0,
    paddingLeft: `${padding_left}px` || 0,
    paddingRight: `${padding_right}px` || 0,
    overflow: "hidden",
    zIndex: element.element_index ?? 0,
  };
};

const MemoizedRenderElement = React.memo(
  ({
    element,
    isLoadingScreen,
    iframeUrl,
    productData,
    iconType,
    handleVariantChange,
    selectedVariants,
    setSelectedVariants,
    navigate,
    setOpen,
    setIframeUrl,
    open,
    iframeUrlInternal,
    handleClose,
    selectedTabs,
    setSelectedTabs,
  }) => {
    const isVisible = element?.props?.is_visible !== false;

    if (!isVisible) return null;

    const style = getElementStyle(element);

    switch (element.type) {
      case "container":
        return (
          <Grid2
            container
            direction={element.props?.direction ?? "row"}
            justifyContent={element.props?.justify_content ?? "flex-start"}
            alignItems={element.props?.align_items ?? "flex-start"}
            style={style}
          >
            {element.children?.map((child) => (
              <MemoizedRenderElement
                key={child.element_id}
                element={child}
                isLoadingScreen={isLoadingScreen}
                iframeUrl={iframeUrl}
                productData={productData}
                iconType={iconType}
                handleVariantChange={handleVariantChange}
                selectedVariants={selectedVariants}
                setSelectedVariants={setSelectedVariants}
                navigate={navigate}
                setOpen={setOpen}
                setIframeUrl={setIframeUrl}
                open={open}
                iframeUrlInternal={iframeUrlInternal}
                handleClose={handleClose}
                selectedTabs={selectedTabs}
                setSelectedTabs={setSelectedTabs}
              />
            ))}
          </Grid2>
        );

      case "image":
        return (
          <Box
            component="img"
            src={element.props?.path}
            alt=""
            sx={{
              ...style,
              objectFit: element.props?.object_fit ?? "contain",
            }}
          />
        );

      case "canvas":
        return (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
            }}
          >
            <iframe
              id="canvas-iframe"
              src={iframeUrl}
              scrolling="no"
              height="100%"
              width="100%"
              frameBorder={0}
              title="Storefront Canvas"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                visibility: isLoadingScreen ? "visible" : "hidden",
                zIndex: 1,
              }}
            />

            {!isLoadingScreen && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Loading
                  text="Loading..."
                  waveSpeed={0.4}
                  waveHeight={1}
                  fontSize={32}
                  color="#192b61"
                  enableFlicker={true}
                  flickerIntensity={3}
                  flickerSpeed={3}
                  isTransparent={true}
                  backgroundColor={"#D3D3D3"}
                />
              </Box>
            )}
          </Box>
        );

      case "video":
        return (
          <video
            src={element.props?.path}
            autoPlay
            loop
            muted
            playsInline
            className="hero-vid"
            preload="none"
            style={{
              width: "100%",
              height: "100%",
              ...style,
            }}
          />
        );

      case "storefront":
        if (element.props?.url_id) {
          return (
            <iframe
              src={element.props?.url_id}
              width="100%"
              height="100%"
              style={{ ...style }}
              frameBorder="0"
              loading="lazy"
            />
          );
        }
        return null;

      case "banner":
        if (element.props?.url_id) {
          return (
            <iframe
              src={element.props?.url_id}
              width="100%"
              height="100%"
              style={{ ...style }}
              frameBorder="0"
              loading="lazy"
            />
          );
        }
        return null;

      case "text":
        return (
          <Typography
            sx={{
              ...style,
              textAlign: element.props?.text_align ?? "left",
              color: element.props?.font_color,
              fontSize: `${element.props?.font_size}px`,
              fontFamily: element.props?.font_family,
              fontWeight: element.props?.font_weight,
              lineHeight: element.props?.line_height,
              letterSpacing: element.props?.letter_spacing,
            }}
          >
            {element.props?.text}
          </Typography>
        );

      case "configurator":
        return (
          <Box>
            {productData?.map((product) => {
              const { product_key, product_name, property } = product;

              const localSelectedTab = selectedTabs[product_key] || 0;

              return (
                <Box key={product_key}>
                  {product_name && (
                    <Typography
                      variant="subtitle"
                      sx={{ fontWeight: 600, color: "#192b61" }}
                    >
                      {product_name}
                    </Typography>
                  )}

                  <Tabs
                    value={localSelectedTab}
                    onChange={(e, newValue) =>
                      setSelectedTabs((prev) => ({
                        ...prev,
                        [product_key]: newValue,
                      }))
                    }
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label={`config-tabs-${product_key}`}
                    sx={{
                      minHeight: "auto",
                      padding: "0px",
                      borderBottom: "1px solid #e0e0e0",
                      "& .MuiTabs-flexContainer": {
                        gap: 1,
                      },
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#192b61",
                        fontFamily: "inherit",
                        minHeight: "auto",
                        padding: "0px",
                      },
                      "& .MuiTab-root.Mui-selected": {
                        color: "#192b61",
                        fontWeight: 600,
                        marginBottom: "5px",
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#192b61",
                      },
                    }}
                  >
                    {property.map((prop) => (
                      <Tab
                        key={prop.property_id}
                        label={prop.display_name}
                        disableRipple
                      />
                    ))}
                  </Tabs>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "240px",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      id={`variant-scroll-${product_key}`}
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        // py: 0.5,
                        overflowX: "auto",
                        overflowY: "hidden",
                        width: "100%",
                        justifyContent: "space-between",
                        scrollBehavior: "smooth",
                        WebkitOverflowScrolling: "touch",
                        scrollbarWidth: "thin",
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#aaa",
                          borderRadius: "4px",
                        },
                      }}
                    >
                      {property[localSelectedTab]?.variants.map((variant) => {
                        const productKey = product.product_key;
                        const propertyId =
                          property[localSelectedTab].property_id;
                        const variantId =
                          variant.variant_id ?? variant.varinat_id;

                        const isSelected =
                          selectedVariants?.[productKey]?.[propertyId] ===
                          variantId;

                        const icon = variant.variant_icons?.find(
                          (icon) => icon.file_type === "L"
                        )?.path;

                        return (
                          <Box
                            key={variantId}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              width: 65,
                              minWidth: 65,
                              borderRadius: 2,
                              cursor: "pointer",
                              transition: "all 0.2s ease-in-out",
                              flexShrink: 0,
                              paddingTop: "2px",
                            }}
                            title={variant.display_name}
                            onClick={() => {
                              handleVariantChange(
                                productKey,
                                propertyId,
                                variantId
                              );
                            }}
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                overflow: "hidden",
                                border: isSelected
                                  ? "2px solid #192b61"
                                  : "2px solid transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: isSelected
                                  ? "#f0f4ff"
                                  : "transparent",
                              }}
                            >
                              <img
                                src={icon}
                                alt={variant.display_name}
                                style={{
                                  width: "80%",
                                  height: "80%",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                }}
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                textAlign: "center",
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {variant.display_name}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        );

      case "button":
        const handleClick = () => {
          const path = element.props?.on_click;
          if (path) {
            navigate(`/${path}`);
          }
        };

        return (
          <Box
            onClick={handleClick}
            style={{
              backgroundColor: element.props?.background_color,
              borderRadius: element.props?.border_radius,
              border: element.props?.is_border
                ? `${element.props?.border}px solid ${element.props?.border_color}`
                : "none",
              color: element.props?.font_color,
              fontFamily: element.props?.font_family,
              fontSize: element.props?.font_size,
              fontStyle: element.props?.font_style,
              fontWeight: element.props?.font_weight,
              letterSpacing: element.props?.letter_spacing,
              lineHeight: element.props?.line_height,
              marginTop: `${element.props?.margin_top}px`,
              marginBottom: `${element.props?.margin_bottom}px`,
              marginLeft: `${element.props?.margin_left}px`,
              marginRight: `${element.props?.margin_right}px`,
              paddingTop: `${element.props?.padding_top}px`,
              paddingBottom: `${element.props?.padding_bottom}px`,
              paddingLeft: `${element.props?.padding_left}px`,
              paddingRight: `${element.props?.padding_right}px`,
              textAlign: element.props?.text_align,
              cursor: element.props?.on_click ? "pointer" : "default",
            }}
          >
            {element.props?.text}
          </Box>
        );

      case "tabButton":
        const handleClickForTab = (event) => {
          event.preventDefault();
          const path = element.props?.on_click;

          if (path) {
            window.open(path, "_blank", "noopener,noreferrer");
          }
        };

        return (
          <Box
            onClick={handleClickForTab}
            style={{
              backgroundColor: element.props?.background_color,
              borderRadius: element.props?.border_radius,
              border: element.props?.is_border
                ? `${element.props?.border}px solid ${element.props?.border_color}`
                : "none",
              color: element.props?.font_color,
              fontFamily: element.props?.font_family,
              fontSize: element.props?.font_size,
              fontStyle: element.props?.font_style,
              fontWeight: element.props?.font_weight,
              letterSpacing: element.props?.letter_spacing,
              lineHeight: element.props?.line_height,
              marginTop: `${element.props?.margin_top}px`,
              marginBottom: `${element.props?.margin_bottom}px`,
              marginLeft: `${element.props?.margin_left}px`,
              marginRight: `${element.props?.margin_right}px`,
              paddingTop: `${element.props?.padding_top}px`,
              paddingBottom: `${element.props?.padding_bottom}px`,
              paddingLeft: `${element.props?.padding_left}px`,
              paddingRight: `${element.props?.padding_right}px`,
              textAlign: element.props?.text_align,
              cursor: element.props?.on_click ? "pointer" : "default",
            }}
          >
            {element.props?.text}
          </Box>
        );

      case "modalButton":
        const handleModalClick = () => {
          const path = element.props?.on_click;
          if (path) {
            setIframeUrl(path);
            setOpen(true);
          }
        };

        return (
          <>
            <Box
              onClick={handleModalClick}
              style={{
                backgroundColor: element.props?.background_color,
                borderRadius: element.props?.border_radius,
                border: element.props?.is_border
                  ? `${element.props?.border}px solid ${element.props?.border_color}`
                  : "none",
                color: element.props?.font_color,
                fontFamily: element.props?.font_family,
                fontSize: element.props?.font_size,
                fontStyle: element.props?.font_style,
                fontWeight: element.props?.font_weight,
                letterSpacing: element.props?.letter_spacing,
                lineHeight: element.props?.line_height,
                marginTop: `${element.props?.margin_top}px`,
                marginBottom: `${element.props?.margin_bottom}px`,
                marginLeft: `${element.props?.margin_left}px`,
                marginRight: `${element.props?.margin_right}px`,
                paddingTop: `${element.props?.padding_top}px`,
                paddingBottom: `${element.props?.padding_bottom}px`,
                paddingLeft: `${element.props?.padding_left}px`,
                paddingRight: `${element.props?.padding_right}px`,
                textAlign: element.props?.text_align,
                cursor: element.props?.on_click ? "pointer" : "default",
              }}
            >
              {element.props?.text}
            </Box>

            <Dialog
              open={open}
              onClose={handleClose}
              maxWidth="xl"
              fullWidth
              style={{
                zIndex: "5000000",
              }}
            >
              <Box display="flex" justifyContent="flex-end">
                <IconButton onClick={handleClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  height: "100vh",
                  overflow: "hidden",
                }}
              >
                <Paper
                  elevation={0}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <iframe
                    src={iframeUrlInternal}
                    width="100%"
                    height="100%"
                    style={{ border: "none" }}
                    title="External Content"
                  />
                </Paper>
              </Box>
            </Dialog>
          </>
        );

      case "contactus":
        return <MeetingScheduler />;
      default:
        return null;
    }
  }
);

const MemoizedRenderPageLayout = React.memo(
  ({
    pageLayoutFinal,
    scrollData,
    HeaderDimensions,
    pageWidthCalc,
    headerPosition,
    isLoadingScreen,
    RenderElement,
    blankMargin,
    iframeUrl,
    productData,
    iconType,
    handleVariantChange,
    selectedVariants,
    setSelectedVariants,
    navigate,
    setOpen,
    setIframeUrl,
    open,
    iframeUrlInternal,
    handleClose,
    selectedTabs,
    setSelectedTabs,
  }) => {
    const headerData = scrollData?.frames?.header;
    let cumulativeTop = 0;
    const currentSectionPositions = [];

    return (
      <>
        {headerData?.is_visible && (
          <div
            style={{
              position: headerData.is_sticky ? "sticky" : "relative",
              zIndex: 5000000,
            }}
          >
            <Header
              headerData={headerData}
              HeaderDimensions={HeaderDimensions}
              pageWidthCalc={pageWidthCalc}
              headerPosition={headerPosition}
              sectionPositions={currentSectionPositions}
            />
          </div>
        )}

        {pageLayoutFinal?.map((section) => {
          const sectionTop = cumulativeTop;
          cumulativeTop += section?.sectionHeight;

          if (section.section_text) {
            currentSectionPositions.push({
              text: section.section_text,
              top: sectionTop,
            });
          }

          return (
            <Box
              key={section.section_id}
              sx={{
                position: "absolute",
                top: sectionTop,
                width: section.sectionWidth,
                height: section.sectionHeight,
                backgroundColor: section.background_color,
                zIndex: section.zIndex,
                marginLeft: `${blankMargin}px`,
                marginRight: `${blankMargin}px`,
              }}
            >
              {section.elements?.map((element) => (
                <RenderElement
                  key={element.element_id}
                  element={element}
                  isLoadingScreen={isLoadingScreen}
                  iframeUrl={iframeUrl}
                  productData={productData}
                  iconType={iconType}
                  handleVariantChange={handleVariantChange}
                  selectedVariants={selectedVariants}
                  setSelectedVariants={setSelectedVariants}
                  navigate={navigate}
                  setOpen={setOpen}
                  setIframeUrl={setIframeUrl}
                  open={open}
                  iframeUrlInternal={iframeUrlInternal}
                  handleClose={handleClose}
                  selectedTabs={selectedTabs}
                  setSelectedTabs={setSelectedTabs}
                />
              ))}
            </Box>
          );
        })}
      </>
    );
  }
);

const Scroll3DAnimation = ({
  HeaderDimensions,
  data,
  isLoadingScreen,
  onSectionPositionsReady,
}) => {
  const navigate = useNavigate();
  const lenisRef = useRef(null);
  const { mutate: sendData } = useSendProgress();
  const { mutate: variantChange } = useSetProductChangeCall();

  const scrollData = data?.data?.scroll;
  const storefrontData = scrollData;

  const maxHeight = scrollData?.frames?.max_height || 945;

  const screenHeight = window.innerHeight;
  const apiMaxHeight = scrollData?.frames?.max_height || 945;

  const finalHeight = screenHeight > apiMaxHeight ? apiMaxHeight : screenHeight;

  const innerMargin = scrollData?.frames?.margin || 0;
  const maxWidth = scrollData?.frames?.max_width || 1280;
  const innerWidth = window.innerWidth;

  let blankMargin = 0;
  let sumMargin = 0;
  let pageWidthCalc = 0;
  let newMaxWidth = 0;

  if (innerWidth > maxWidth) {
    newMaxWidth = maxWidth;
    blankMargin = (innerWidth - newMaxWidth) / 2;
    sumMargin = 2 * innerMargin * newMaxWidth;
    pageWidthCalc = newMaxWidth - sumMargin;
  } else {
    newMaxWidth = innerWidth;
    blankMargin = 0;
    sumMargin = 2 * innerMargin * innerWidth;
    pageWidthCalc = innerWidth - sumMargin;
  }
  const headerPosition = blankMargin + sumMargin / 2;

  const templates = scrollData?.frames?.templates;

  const sectionData = scrollData?.layers?.find((layer) => layer?.is_canvas);

  const memoizedProcessedSectionData = useMemo(() => {
    return mergeSectionWithTemplates(sectionData, templates);
  }, [sectionData, templates]);

  const positionType = scrollData?.frames?.position_type;

  const sessionID = data?.data?.sessionID;
  const productData = scrollData?.products;

  const screenTypeDetails = scrollData?.frames?.screen_type;
  const defaultScreenTypeId = screenTypeDetails?.find(
    (type) => type?.is_default
  )?.screen_type_id;

  const iframeUrl = useMemo(() => {
    if (!scrollData || !sessionID) return "";
    return `${process.env.REACT_APP_CANVAS_URL}/?scroll=${scrollData.scroll_id}&session=${sessionID}&page_height=100&page_width=100`;
  }, [scrollData?.scroll_id, sessionID]);

  const screenType = useResponsiveScreenType(screenTypeDetails);

  const [selectedVariants, setSelectedVariants] = useState({});

  const handleVariantChange = useCallback(
    (productId, propertyId, variantId) => {
      const payload = {
        session_id: sessionID,
        message: {
          type: "change_variant",
          message: {
            product_key: productId,
            property_id: propertyId,
            variant_id: variantId,
          },
        },
      };

      setSelectedVariants((prev) => ({
        ...prev,
        [productId]: {
          ...(prev[productId] || {}),
          [propertyId]: variantId,
        },
      }));

      variantChange(payload);
    },
    [sessionID, variantChange]
  );

  const getValueByScreenTypePriority = (
    arr,
    screenType,
    defaultScreenTypeId
  ) => {
    if (!Array.isArray(arr)) return null;

    const valueFromScreenType = screenType
      ? arr.find((item) => item.screen_type_id === screenType)?.value
      : null;

    if (valueFromScreenType != null) return valueFromScreenType;

    return (
      arr.find((item) => item.screen_type_id === defaultScreenTypeId)?.value ??
      null
    );
  };

  const width = getValueByScreenTypePriority(
    scrollData?.frames?.width,
    screenType,
    defaultScreenTypeId
  );
  const height = getValueByScreenTypePriority(
    scrollData?.frames?.height,
    screenType,
    defaultScreenTypeId
  );

  const initialPageHeight = useRef(window.innerHeight);
  const initialPageWidth = useRef(window.innerWidth);

  // const pageHeight = parseFloat(height) * initialPageHeight?.current;
  const pageHeight = parseFloat(height) * finalHeight;

  const pageWidth = parseFloat(width) * initialPageWidth?.current;

  const pageLayoutFinal = useMemo(() => {
    if (!storefrontData) return [];
    return generatePageLayout(
      storefrontData,
      screenType,
      defaultScreenTypeId,
      newMaxWidth,
      finalHeight
    );
  }, [
    storefrontData,
    screenType,
    defaultScreenTypeId,
    newMaxWidth,
    finalHeight,
  ]);

  const [open, setOpen] = useState(false);
  const [iframeUrlInternal, setIframeUrl] = useState("");
  const [sectionPositions, setSectionPositions] = useState([]);

  const [selectedTabs, setSelectedTabs] = useState({});

  const isMobile = useMediaQuery("(max-width:600px)");
  const iconType = isMobile ? "S" : "L";

  const handleClose = useCallback(() => {
    setOpen(false);
    setIframeUrl("");
  }, [setOpen, setIframeUrl]);

  useEffect(() => {
    const calculatedPositions = [];
    let currentCumulativeTop = 0;

    pageLayoutFinal.forEach((section) => {
      if (section.section_text) {
        calculatedPositions.push({
          text: section.section_text,
          top: currentCumulativeTop,
        });
      }
      currentCumulativeTop += section.sectionHeight;
    });

    setSectionPositions(calculatedPositions);
  }, [pageLayoutFinal]);

  useEffect(() => {
    if (
      Array.isArray(productData) &&
      productData.length > 0 &&
      Object.keys(selectedVariants).length === 0
    ) {
      const initialVariants = {};

      productData.forEach((product) => {
        const productId = product.product_key;
        initialVariants[productId] = {};

        product.property.forEach((property) => {
          const defaultVariant = property.variants.find((v) => v.is_default);
          if (defaultVariant) {
            initialVariants[productId][property.property_id] =
              defaultVariant.varinat_id;
          }
        });
      });

      setSelectedVariants(initialVariants);
    }
  }, [productData, selectedVariants]);

  useEffect(() => {
    const calculatedPositions = [];
    let currentCumulativeTop = 0;

    pageLayoutFinal.forEach((section) => {
      if (section.section_text) {
        calculatedPositions.push({
          text: section.section_text,
          top: currentCumulativeTop,
        });
      }
      currentCumulativeTop += section.sectionHeight;
    });

    setSectionPositions(calculatedPositions);

    if (onSectionPositionsReady) {
      onSectionPositionsReady(calculatedPositions);
    }
  }, [pageLayoutFinal, onSectionPositionsReady]);

  return (
    <div>
      {scrollData && (
        <div
          style={{
            height: `${pageHeight}px`,
            width: `${pageWidth}px`,
          }}
        >
          <MemoizedRenderPageLayout
            pageLayoutFinal={pageLayoutFinal}
            scrollData={scrollData}
            HeaderDimensions={HeaderDimensions}
            pageWidthCalc={pageWidthCalc}
            headerPosition={headerPosition}
            isLoadingScreen={isLoadingScreen}
            RenderElement={MemoizedRenderElement}
            blankMargin={blankMargin}
            iframeUrl={iframeUrl}
            productData={productData}
            iconType={iconType}
            handleVariantChange={handleVariantChange}
            selectedVariants={selectedVariants}
            setSelectedVariants={setSelectedVariants}
            navigate={navigate}
            setOpen={setOpen}
            setIframeUrl={setIframeUrl}
            open={open}
            iframeUrlInternal={iframeUrlInternal}
            handleClose={handleClose}
            selectedTabs={selectedTabs}
            setSelectedTabs={setSelectedTabs}
          />
        </div>
      )}
    </div>
  );
};

export default Scroll3DAnimation;
